/**
 * SENTRY-FETCH CACHE CLAMP — fixes the auth-probe thrash window.
 *
 * sentry-fetch caches "authenticated until expires_at − CLOCK_SKEW (5 min)"
 * per origin in sessionStorage. Our API tokens live ~15–20 min, so for the
 * LAST 5 MINUTES of every token lifetime that arithmetic produces a stamp
 * that is already in the past: every request wave re-probes /sso/login
 * (~0.4 s, serialized ahead of the wave), receives a truthful expires_at,
 * writes another already-stale stamp, and repeats — measured in prod as 12
 * login calls in a single page load (2026-08-27). The probe traffic buys
 * nothing: the token is still valid the whole time.
 *
 * Fix: when the stored stamp is in the past, recover the TRUE expiry
 * (stamp + CLOCK_SKEW) and rewrite the stamp to (trueExpiry − SAFETY_MS),
 * or trueExpiry itself when even that safety margin has passed. The stamp
 * is NEVER written beyond the true expiry, so it cannot claim validity for
 * a dead token; the margin only shrinks, never lies.
 *
 * Worst case (client clock drift beyond SAFETY_MS): one request rides a
 * just-dead token and fails. Verified safe: authorizer denials carry no
 * CORS headers, so the browser surfaces them as fetch REJECTIONS, which is
 * exactly sentry-fetch's recovery path (clear cache → re-authenticate →
 * retry, attempt-capped at 3, and every retry re-probes /sso/login rather
 * than trusting this cache — verified in the installed 4.0.9 source). One
 * recoverable stall replaces a five-minute thrash window.
 *
 * DELIBERATE COUPLING: this reads/writes sentry-fetch's private storage
 * (key `sentry-auth: <origin>`, value = epoch-ms string, skew = 5 min).
 * The contract is pinned by sentryFetchContract.canary.test.ts, which
 * drives the REAL installed library — an upgrade that changes the format
 * fails the build, never prod auth. Delete this module when the upstream
 * fix (margin proportional to token lifetime) ships.
 */

/** Mirror of sentry-fetch's CLOCK_SKEW (pinned by the canary test). */
export const SENTRY_CLOCK_SKEW_MS = 5 * 60 * 1000;

/**
 * Residual early-recheck margin we keep for clock drift and request flight
 * time. Small relative to token lifetime by construction (falls back to the
 * true expiry when it would land in the past).
 */
export const CLAMP_SAFETY_MS = 60_000;

/** Mirror of sentry-fetch's getSessionStorageKey (pinned by the canary test). */
export function sentryAuthStorageKey(origin: string): string {
  return `sentry-auth: ${origin}`;
}

/**
 * OUR shadow key (not sentry-fetch's): the true expiry this module derived
 * when it last clamped `origin`. Needed for idempotence — a re-clamp of a
 * stamp WE wrote must reuse the original true expiry rather than re-deriving
 * (stamp + CLOCK_SKEW) from our own output, which would inflate the expiry
 * by (CLOCK_SKEW − CLAMP_SAFETY_MS) on every pass and eventually claim
 * validity for a dead token.
 */
export function clampShadowKey(origin: string): string {
  return `sentry-auth-clamp-true-expiry: ${origin}`;
}

/**
 * Repair an already-stale auth stamp for `origin` while its token is still
 * truly valid. Idempotent: re-invocations converge on the true expiry and
 * never write beyond it. No-ops when: no stamp, unparseable stamp (unknown
 * format — never touch what we don't recognize), stamp still valid, or
 * token truly expired (the next probe must do the real re-mint).
 *
 * Returns true ONLY when a stamp was actually rewritten, so the call site
 * can emit an observability counter (Auth.ClampRepair) — the fix's effect
 * is otherwise invisible outside waterfall archaeology.
 */
export function clampSentryAuthCache(
  origin: string,
  now = Date.now(),
): boolean {
  const key = sentryAuthStorageKey(origin);
  let raw: string | null;
  let shadowRaw: string | null;
  try {
    raw = sessionStorage.getItem(key);
    shadowRaw = sessionStorage.getItem(clampShadowKey(origin));
  } catch {
    return false; // storage unavailable: sentry-fetch is equally blind — nothing to fix
  }
  if (raw === null) return false;
  const stamp = Number(raw);
  if (!Number.isFinite(stamp)) return false;
  if (stamp > now) return false; // note still valid — the healthy case

  // A stamp that matches our own previous write (with or without margin)
  // reuses the remembered true expiry; anything else is a fresh library
  // write, for which trueExpiry = stamp + CLOCK_SKEW by the pinned contract.
  const shadow = Number(shadowRaw);
  const isOurWrite =
    Number.isFinite(shadow) &&
    (stamp === shadow - CLAMP_SAFETY_MS || stamp === shadow);
  const trueExpiry = isOurWrite ? shadow : stamp + SENTRY_CLOCK_SKEW_MS;
  if (trueExpiry <= now) return false; // token genuinely dead — let the probe re-mint

  const withMargin = trueExpiry - CLAMP_SAFETY_MS;
  const clamped = withMargin > now ? withMargin : trueExpiry;
  try {
    // Shadow FIRST: if only the shadow lands, the untouched library stamp
    // simply re-derives the same true expiry next time. The reverse order
    // would persist OUR stamp without its shadow, and the next clamp would
    // re-derive from our own output — the exact inflation the shadow guards
    // against.
    sessionStorage.setItem(clampShadowKey(origin), String(trueExpiry));
    sessionStorage.setItem(key, String(clamped));
    return true;
  } catch {
    // Write failed: behavior degrades to today's (probe per wave), never worse.
    return false;
  }
}
